import { useEffect } from "react";
import { useSelector } from "react-redux";
import { showToast } from "../../components/toast";
import actions from "../../actions/actions";
import { useUpdateState } from "../../utilities/utilities";
import { UserRole, DEFAULT_STUDENT_LABEL, DEFAULT_FACULTY_LABEL } from "../../utilities/constants";

export default function AccountPermissions() {
  let user = useSelector((store) => store.user);
  let isAdmin = user?.userType === UserRole.Admin;

  let [state, updateState] = useUpdateState({
    // Login Access
    allowStudentLogin: false,
    allowFacultyLogin: false,
    emergencyOnly: false,
    optOutRecovery: false,
    loginLoaded: false,
    // Dashboard Access
    studentViewOnly: false,
    facultyViewOnly: false,
    studentLabel: "Student",
    facultyLabel: "Faculty",
    passwordRecoveryEnabled: true,
    loaded: false,
    dashboardLoaded: false,
  });

  useEffect(() => {
    if (!isAdmin) return;

    actions.account.getLoginAccess().then((result) => {
      if (result?.success) {
        updateState({
          allowStudentLogin: result.allowStudentLogin,
          allowFacultyLogin: result.allowFacultyLogin,
          emergencyOnly: result.emergencyOnly,
          optOutRecovery: result.optOutRecovery,
          studentLabel: result.studentLabel || DEFAULT_STUDENT_LABEL,
          facultyLabel: result.facultyLabel || DEFAULT_FACULTY_LABEL,
          loginLoaded: true,
        });
      }
    });

    actions.account.getPermissions().then((result) => {
      if (result?.success) {
        updateState({
          studentViewOnly: result.studentViewOnly,
          facultyViewOnly: result.facultyViewOnly,
          studentLabel: result.studentLabel || DEFAULT_STUDENT_LABEL,
          facultyLabel: result.facultyLabel || DEFAULT_FACULTY_LABEL,
          passwordRecoveryEnabled: result.passwordRecoveryEnabled,
          loaded: true,
          dashboardLoaded: true,
        });
      }
    });
  }, [user?.userType]);

  async function handleLoginToggle(field, value) {
    const prev = state[field];
    updateState({ [field]: value });

    const allowStudentLogin = field === "allowStudentLogin" ? value : state.allowStudentLogin;
    const allowFacultyLogin = field === "allowFacultyLogin" ? value : state.allowFacultyLogin;
    const emergencyOnly     = field === "emergencyOnly"     ? value : state.emergencyOnly;
    const optOutRecovery    = field === "optOutRecovery"    ? value : state.optOutRecovery;

    const result = await actions.account.saveLoginAccess(
      allowStudentLogin,
      allowFacultyLogin,
      emergencyOnly,
      optOutRecovery
    );
    if (!result?.success) {
      updateState({ [field]: prev });
      showToast("error", "Failed to save setting. Please try again.");
    }
  }

  async function handleDashboardToggle(field, value) {
    const prev = state[field];
    updateState({ [field]: value });

    const studentViewOnly = field === "studentViewOnly" ? value : state.studentViewOnly;
    const facultyViewOnly = field === "facultyViewOnly" ? value : state.facultyViewOnly;

    const result = await actions.account.saveDashboardAccess(studentViewOnly, facultyViewOnly);
    if (!result?.success) {
      updateState({ [field]: prev });
      showToast("error", "Failed to save setting. Please try again.");
    }
  }

  async function handleSecurityToggle(value) {
    const prev = state.passwordRecoveryEnabled;
    updateState({ passwordRecoveryEnabled: value });

    const result = await actions.account.saveSecuritySettings(value);
    if (!result?.success) {
      updateState({ passwordRecoveryEnabled: prev });
      showToast("error", "Failed to save setting. Please try again.");
    }
  }

  if (!isAdmin) return null;

  return (
    <div className="account-permissions">

      {/* ── Login Access ── */}
      <span className="account-permissions__title">Login Access</span>
      <div className="account-permissions__settings">
        <div className="account-permissions__setting">
          <div className="account-permissions__setting-top">
            <span className="account-permissions__setting-label">{state.studentLabel} Login</span>
            <button
              className={`account-permissions__toggle${state.allowStudentLogin ? " account-permissions__toggle--on" : ""}`}
              onClick={() => handleLoginToggle("allowStudentLogin", !state.allowStudentLogin)}
              aria-pressed={state.allowStudentLogin}
              aria-label={`${state.studentLabel} Login`}
            />
          </div>
          <span className="account-permissions__setting-hint">Allow {state.studentLabel.toLowerCase()}s to log in to the portal</span>
        </div>
        <div className="account-permissions__setting">
          <div className="account-permissions__setting-top">
            <span className="account-permissions__setting-label">{state.facultyLabel} Login</span>
            <button
              className={`account-permissions__toggle${state.allowFacultyLogin ? " account-permissions__toggle--on" : ""}`}
              onClick={() => handleLoginToggle("allowFacultyLogin", !state.allowFacultyLogin)}
              aria-pressed={state.allowFacultyLogin}
              aria-label={`${state.facultyLabel} Login`}
            />
          </div>
          <span className="account-permissions__setting-hint">Allow {state.facultyLabel.toLowerCase()} members to log in to the portal</span>
        </div>
        <div className="account-permissions__setting">
          <div className="account-permissions__setting-top">
            <span className="account-permissions__setting-label">Emergency Only Contacts</span>
            <button
              className={`account-permissions__toggle${state.emergencyOnly ? " account-permissions__toggle--on" : ""}`}
              onClick={() => handleLoginToggle("emergencyOnly", !state.emergencyOnly)}
              aria-pressed={state.emergencyOnly}
              aria-label="Emergency Only Contacts"
            />
          </div>
          <span className="account-permissions__setting-hint">Restrict certain contacts to emergency messages only</span>
        </div>
        <div className="account-permissions__setting">
          <div className="account-permissions__setting-top">
            <span className="account-permissions__setting-label">Opt Out of Password Recovery System</span>
            <button
              className={`account-permissions__toggle${state.optOutRecovery ? " account-permissions__toggle--on" : ""}`}
              onClick={() => handleLoginToggle("optOutRecovery", !state.optOutRecovery)}
              aria-pressed={state.optOutRecovery}
              aria-label="Opt Out of Password Recovery System"
            />
          </div>
        </div>
      </div>

      <div className="account-permissions__divider" />

      {/* ── Dashboard Access (functional) ── */}
      <span className="account-permissions__title">Dashboard Access</span>
      <div className="account-permissions__settings">
        <div className="account-permissions__setting">
          <div className="account-permissions__setting-top">
            <span className="account-permissions__setting-label">
              View-Only Dashboard for {state.studentLabel}s
            </span>
            <button
              className={`account-permissions__toggle${state.studentViewOnly ? " account-permissions__toggle--on" : ""}`}
              onClick={() => handleDashboardToggle("studentViewOnly", !state.studentViewOnly)}
              aria-pressed={state.studentViewOnly}
              aria-label={`View-Only Dashboard for ${state.studentLabel}s`}
            />
          </div>
          <span className="account-permissions__setting-hint">
            {state.studentLabel}s see a read-only version of the dashboard
          </span>
        </div>
        <div className="account-permissions__setting">
          <div className="account-permissions__setting-top">
            <span className="account-permissions__setting-label">
              View-Only Dashboard for {state.facultyLabel}s
            </span>
            <button
              className={`account-permissions__toggle${state.facultyViewOnly ? " account-permissions__toggle--on" : ""}`}
              onClick={() => handleDashboardToggle("facultyViewOnly", !state.facultyViewOnly)}
              aria-pressed={state.facultyViewOnly}
              aria-label={`View-Only Dashboard for ${state.facultyLabel}s`}
            />
          </div>
          <span className="account-permissions__setting-hint">
            {state.facultyLabel} see a read-only version of the dashboard
          </span>
        </div>
      </div>

      <div className="account-permissions__divider" />

      {/* ── Security (functional) ── */}
      <span className="account-permissions__title">Security</span>
      <div className="account-permissions__settings">
        <div className="account-permissions__setting">
          <div className="account-permissions__setting-top">
            <span className="account-permissions__setting-label">Password Recovery</span>
            <button
              className={`account-permissions__toggle${state.passwordRecoveryEnabled ? " account-permissions__toggle--on" : ""}`}
              onClick={() => handleSecurityToggle(!state.passwordRecoveryEnabled)}
              aria-pressed={state.passwordRecoveryEnabled}
              aria-label="Password Recovery"
            />
          </div>
          <span className="account-permissions__setting-hint">Users can reset their password via email</span>
        </div>
      </div>

    </div>
  );
}
